import type { ShellConfig } from '@akademi/site-builder-runtime';
import type {
  HeaderCta,
  HeaderFeatured,
  HeaderLink,
  HeaderMegaColumn,
  HeaderMenuItem,
  SpatialKitchenHeaderProps,
} from './SpatialKitchenHeader';
import type { Localized } from '../shared/primitives';
import type {
  FooterColumn,
  FooterLinkItem,
  FooterNewsletter,
  FooterSavedDesigns,
  SpatialKitchenFooterProps,
} from '../blocks/SpatialKitchenFooter';

/**
 * Maps the operator-authored Shell config (the `SiteShell` block's props, as
 * edited in the admin Shell Editor) onto this theme's header and footer.
 *
 * <p>Standard shell fields are used where they exist (`brand`, `header.menu`,
 * `header.actions`, `footer.groups`, `footer.utilityLinks`, `footer.tagline`,
 * `footer.copyright`), so an operator edits the kitchen chrome with the same
 * editor as any other theme. Two theme-specific objects ride on the footer's
 * open index signature: `footer.newsletter` and `footer.savedDesigns`.
 */

type Loose = Record<string, unknown>;

const asObject = (value: unknown): Loose =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Loose) : {};

const asString = (value: unknown): string | undefined => (typeof value === 'string' && value ? value : undefined);

function headerLinks(value: unknown): HeaderLink[] {
  return Array.isArray(value)
    ? value.map(asObject).map((link) => ({
        label: link.label as HeaderLink['label'],
        href: asString(link.href),
        description: link.description as HeaderLink['description'],
      }))
    : [];
}

/**
 * A department's mega menu, in the shared `MenuLink.megamenu` shape the Shell
 * Editor authors (`columns[].links[]`, `featured`). `featured.eyebrow` rides on
 * the open object, as the footer's extras do.
 */
function megaMenu(value: unknown): Pick<HeaderMenuItem, 'columns' | 'featured'> {
  const mega = asObject(value);
  const featured = asObject(mega.featured);
  const columns: HeaderMegaColumn[] = Array.isArray(mega.columns)
    ? mega.columns.map(asObject).map((column) => ({
        label: column.label as HeaderMegaColumn['label'],
        links: headerLinks(column.links),
      }))
    : [];
  return {
    columns,
    featured: Object.keys(featured).length
      ? {
          eyebrow: featured.eyebrow as HeaderFeatured['eyebrow'],
          title: featured.title as HeaderFeatured['title'],
          description: featured.description as HeaderFeatured['description'],
          ctaLabel: featured.ctaLabel as HeaderFeatured['ctaLabel'],
          ctaHref: asString(featured.ctaHref),
          image: asString(featured.image),
        }
      : undefined,
  };
}

function menuItems(value: unknown): HeaderMenuItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(asObject)
    .filter((entry) => entry.kind === undefined || entry.kind === 'link' || entry.kind === 'group')
    .map((entry) => ({
      label: entry.label as HeaderMenuItem['label'],
      href: asString(entry.href),
      links: menuItems(entry.links),
      ...(entry.dropdownStyle === 'flat' ? {} : megaMenu(entry.megamenu)),
    }));
}

/** An action's href, found by the icon or href the operator gave it. */
function actionHref(actions: Loose[], match: (action: Loose) => boolean): string | undefined {
  return asString(actions.find(match)?.href);
}

/** Icons that make an action one of the header's tools rather than a button in the category bar. */
const TOOL_ICONS = new Set(['heart', 'user', 'account', 'basket', 'cart']);

function localizedList(value: unknown): Localized[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is Localized => typeof entry === 'string' || (Boolean(entry) && typeof entry === 'object'))
    : [];
}

export function headerPropsFromShell(config: ShellConfig | Loose | null | undefined): SpatialKitchenHeaderProps {
  const cfg = asObject(config);
  const brand = asObject(cfg.brand);
  const header = asObject(cfg.header);
  const search = asObject(cfg.search);
  const actions = Array.isArray(header.actions) ? header.actions.map(asObject) : [];
  const hrefIs = (fragment: string) => (action: Loose) =>
    action.icon === fragment || (typeof action.href === 'string' && action.href.includes(fragment));

  return {
    brandName: brand.name as SpatialKitchenHeaderProps['brandName'],
    // The shop's motto, under its name in the header: a few words, not the
    // sentence `brand.tagline` holds for the footer. It rides on the config's
    // open index signature, as the footer's newsletter and saved-designs
    // objects do.
    motto: brand.motto as SpatialKitchenHeaderProps['motto'],
    logo: asString(brand.logo),
    logoHref: asString(brand.logoHref),
    logoHeight: typeof brand.logoHeight === 'number' ? brand.logoHeight : undefined,
    menu: menuItems(header.menu),
    // The utility bar rides on the header's open index signature, as `motto` does.
    usps: localizedList(header.usps),
    utilityLinks: headerLinks(header.utilityLinks),
    ctas: actions
      .filter((action) => !(typeof action.icon === 'string' && TOOL_ICONS.has(action.icon)))
      .map((action) => ({
        label: action.label as HeaderCta['label'],
        href: asString(action.href),
        variant: action.variant === 'primary' ? 'primary' : 'secondary',
      })),
    searchHref: asString(search.resultsHref),
    searchPlaceholder: header.searchPlaceholder as SpatialKitchenHeaderProps['searchPlaceholder'],
    // A saved-designs tool is an action with the heart icon. Matching on an href
    // containing "planner" (as this once did) also caught a "Design your
    // kitchen" button that links to the planner.
    savedHref: actionHref(actions, (action) => action.icon === 'heart'),
    accountHref: actionHref(actions, (action) => action.icon === 'user' || action.icon === 'account') ?? actionHref(actions, hrefIs('account')),
    basketHref: actionHref(actions, (action) => action.icon === 'basket' || action.icon === 'cart') ?? actionHref(actions, hrefIs('cart')),
    sticky: header.sticky !== false,
    phone: asString(header.phone),
    openingHours: header.openingHours as SpatialKitchenHeaderProps['openingHours'],
  };
}

function linkList(value: unknown): FooterLinkItem[] {
  return Array.isArray(value)
    ? value.map(asObject).map((link) => ({ label: link.label as FooterLinkItem['label'], href: asString(link.href) }))
    : [];
}

export function footerPropsFromShell(config: ShellConfig | Loose | null | undefined): SpatialKitchenFooterProps {
  const cfg = asObject(config);
  const brand = asObject(cfg.brand);
  const footer = asObject(cfg.footer);
  const groups = Array.isArray(footer.groups) ? footer.groups.map(asObject) : [];

  return {
    brandName: brand.name as SpatialKitchenFooterProps['brandName'],
    tagline: (footer.tagline ?? brand.tagline) as SpatialKitchenFooterProps['tagline'],
    columns: groups.map(
      (group): FooterColumn => ({
        title: (group.label ?? group.title) as FooterColumn['title'],
        links: linkList(group.links),
      }),
    ),
    legalLinks: linkList(footer.utilityLinks),
    copyright: footer.copyright as SpatialKitchenFooterProps['copyright'],
    newsletter: asObject(footer.newsletter) as FooterNewsletter,
    savedDesigns: asObject(footer.savedDesigns) as FooterSavedDesigns,
  };
}
